import axios from 'axios';

// Empty base = same-origin relative requests (the ingress routes /api -> backend).
// Local dev sets REACT_APP_API_URL explicitly (e.g. http://localhost:8000).
const API_URL = process.env.REACT_APP_API_URL || '';

const client = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const login = async (username, password) => {
  const response = await client.post('/api/auth/login', { username, password });
  return response.data;
};

export const logout = async () => {
  const response = await client.post('/api/auth/logout');
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await client.get('/api/auth/me');
  return response.data;
};

// User Management API calls (SuperAdmin only)
export const listUsers = async () => {
  const response = await client.get('/api/auth/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await client.post('/api/auth/users', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await client.put(`/api/auth/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await client.delete(`/api/auth/users/${userId}`);
  return response.data;
};

// Pipeline API calls
export const getPipelines = async () => {
  const response = await client.get('/api/dashboard/pipelines');
  return response.data;
};

export const createPipeline = async (pipelineData) => {
  const response = await client.post('/api/admin/pipelines', pipelineData);
  return response.data;
};

export const updatePipeline = async (pipelineId, updateData) => {
  const response = await client.put(`/api/admin/pipelines/${pipelineId}`, updateData);
  return response.data;
};

export const deletePipeline = async (pipelineId) => {
  const response = await client.delete(`/api/admin/pipelines/${pipelineId}`);
  return response.data;
};

export const startPipeline = async (pipelineId) => {
  const response = await client.post(`/api/admin/pipelines/${pipelineId}/start`);
  return response.data;
};

export const stopPipeline = async (pipelineId) => {
  const response = await client.post(`/api/admin/pipelines/${pipelineId}/stop`);
  return response.data;
};

// Metrics API calls
export const getPipelineMetrics = async (pipelineId, hours = 1) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/metrics`, {
    params: { hours },
  });
  return response.data;
};

export const getDashboardMetrics = async () => {
  const response = await client.get('/api/dashboard/metrics');
  return response.data;
};

// Schedule API calls (legacy cron-based)
export const createSchedule = async (pipelineId, scheduleData) => {
  const response = await client.post(
    `/api/admin/pipelines/${pipelineId}/schedules`,
    scheduleData
  );
  return response.data;
};

export const getSchedules = async (pipelineId) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/schedules`);
  return response.data;
};

export const deleteSchedule = async (scheduleId) => {
  const response = await client.delete(`/api/admin/schedules/${scheduleId}`);
  return response.data;
};

export const toggleSchedule = async (scheduleId) => {
  const response = await client.put(`/api/admin/schedules/${scheduleId}/toggle`);
  return response.data;
};

// Schedule Window API calls (time-window based scheduling)
export const createScheduleWindow = async (pipelineId, scheduleData) => {
  const response = await client.post(
    `/api/admin/pipelines/${pipelineId}/schedule-windows`,
    scheduleData
  );
  return response.data;
};

export const getScheduleWindows = async (pipelineId) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/schedule-windows`);
  return response.data;
};

export const getScheduleWindow = async (scheduleId) => {
  const response = await client.get(`/api/schedule-windows/${scheduleId}`);
  return response.data;
};

export const updateScheduleWindow = async (scheduleId, updateData) => {
  const response = await client.put(
    `/api/admin/schedule-windows/${scheduleId}`,
    updateData
  );
  return response.data;
};

export const deleteScheduleWindow = async (scheduleId) => {
  const response = await client.delete(`/api/admin/schedule-windows/${scheduleId}`);
  return response.data;
};

export const toggleScheduleWindow = async (scheduleId) => {
  const response = await client.put(`/api/admin/schedule-windows/${scheduleId}/toggle`);
  return response.data;
};

// Error Simulation API calls
export const toggleABRError = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/errors/abr`);
  return response.data;
};

export const toggleOriginError = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/errors/origin`);
  return response.data;
};

export const toggleCDNError = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/errors/cdn`);
  return response.data;
};

export const toggleHeaderCheckError = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/errors/header-check`);
  return response.data;
};

export const toggleBlackScreenError = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/errors/black-screen`);
  return response.data;
};

export const getErrorStates = async (pipelineId) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/errors`);
  return response.data;
};

// Get available formats and CDNs for error filtering
export const getErrorFilters = async (pipelineId) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/errors/filters`);
  return response.data;
};

// Toggle format-based error (HLS or DASH)
export const toggleFormatError = async (pipelineId, formatType) => {
  const response = await client.post(
    `/api/pipelines/${pipelineId}/errors/format/${formatType.toLowerCase()}`
  );
  return response.data;
};

// Toggle CDN-specific error (e.g., CLOUDFRONT, AKAMAI, FASTLY)
export const toggleCDNSpecificError = async (pipelineId, cdnName) => {
  const response = await client.post(
    `/api/pipelines/${pipelineId}/errors/cdn-specific/${cdnName.toLowerCase()}`
  );
  return response.data;
};

// E2E Metrics API calls
export const getE2EMetricsStatus = async (pipelineId) => {
  const response = await client.get(`/api/pipelines/${pipelineId}/e2e-metrics`);
  return response.data;
};

export const pushE2EMetrics = async (pipelineId) => {
  const response = await client.post(`/api/pipelines/${pipelineId}/e2e-metrics/push`);
  return response.data;
};

export default client;